/**
 * Microphone selection helpers for the voice call (useCallMode).
 *
 * Pure, framework-free module — no React, no AudioContext. Two
 * responsibilities:
 *
 * 1. Ranking candidate `audioinput` devices for the switcher menu and the
 *    "Try another mic" suggestion on silence advisories, so every surface
 *    agrees on "which mic is best next". This logic used to live inline
 *    inside useCallMode's `startCall` closure; it moved here as the single
 *    source of truth after a live incident: the (since-removed) silent-mic
 *    auto-fallback grabbed an iPhone Continuity mic over the user's
 *    Bluetooth headset — technically working, terrible experience.
 *    Tiers (ascending = better): 0 = paired with an audio OUTPUT (a headset —
 *    the device the user is actually hearing Sara through), 1 = plain mic,
 *    2 = Continuity/iPhone/iPad, 3 = virtual/loopback (dead last). Virtual is
 *    checked BEFORE Continuity so a label like "iPhone Virtual Loopback"
 *    lands in tier 3, not 2.
 * 2. Remembering the user's EXPLICITLY CHOSEN mic across calls
 *    (localStorage). Manual switcher picks only (FB28) — the OS/browser
 *    default wins otherwise, so system routing (e.g. car Bluetooth) is never
 *    overridden by a stale memory.
 */

/**
 * Key is versioned: v1 (`sara.call.micDeviceId`) was ALSO written by the
 * removed signal-based auto-persistence — any mic that picked up room noise
 * got remembered, including the silent-mic auto-fallback's picks. That
 * poisoned real users' storage with a phone mic while audio played through
 * car-Bluetooth speakers → the mic heard Sara through the speakers on every
 * subsequent call (FB28 echo incident). v1 values are untrusted by
 * construction, so the key changed rather than migrating them; reads sweep
 * the orphaned v1 entry.
 */
export const PREFERRED_MIC_STORAGE_KEY = 'sara.call.micDeviceId.v2';
const LEGACY_PREFERRED_MIC_STORAGE_KEY = 'sara.call.micDeviceId';

const CONTINUITY_DEVICE_RE = /iphone|ipad|continuity/i;
const VIRTUAL_DEVICE_RE =
  /virtual|loopback|blackhole|soundflower|aggregate|monitor/i;
// Display-only refinement of tier 1 ("plain mic") for the device menu's
// "Built-in" sublabel — not a ranking input, so it doesn't exist in the
// tier function below.
const BUILTIN_DEVICE_RE = /built-?in|internal|macbook/i;

// ─── Preferred-mic storage ──────────────────────────────────────
//
// localStorage access is guarded end-to-end: `window` doesn't exist during
// SSR/build, and even in-browser `localStorage` can throw (private-browsing
// quota, disabled storage via enterprise policy) — a dead mic-memory feature
// must never break call start.

export function readPreferredMicId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    // Sweep the orphaned v1 entry (see the key's version comment).
    window.localStorage.removeItem(LEGACY_PREFERRED_MIC_STORAGE_KEY);
    return window.localStorage.getItem(PREFERRED_MIC_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storePreferredMicId(id: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PREFERRED_MIC_STORAGE_KEY, id);
  } catch {
    // Storage can throw (private mode, quota, disabled) — losing the
    // cross-session preference isn't worth surfacing an error for.
  }
}

export function clearPreferredMicId(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(PREFERRED_MIC_STORAGE_KEY);
  } catch {
    // See storePreferredMicId.
  }
}

// ─── Candidate ranking ──────────────────────────────────────────

function fallbackTier(
  device: MediaDeviceInfo,
  outputGroupIds: ReadonlySet<string>
): number {
  // Order matters: an iPhone's virtual/loopback input still carries
  // "iPhone" in its label, so the virtual check must win first or it would
  // be misclassified as a (better-ranked) Continuity mic.
  if (VIRTUAL_DEVICE_RE.test(device.label)) return 3;
  if (CONTINUITY_DEVICE_RE.test(device.label)) return 2;
  if (device.groupId && outputGroupIds.has(device.groupId)) return 0;
  return 1;
}

/**
 * GroupIds of every audiooutput in `devices` (the full enumerateDevices()
 * list) — the "is this mic paired with an output, i.e. a headset?" check
 * shared by ranking and menu classification. Build it once per device list
 * and pass it to classifyMicDevice when classifying many devices.
 */
export function buildOutputGroupIds(
  devices: readonly MediaDeviceInfo[]
): ReadonlySet<string> {
  return new Set(
    devices
      .filter((d) => d.kind === 'audiooutput' && d.groupId)
      .map((d) => d.groupId)
  );
}

/**
 * Filters `devices` down to usable `audioinput` candidates (permission
 * already granted, so labels/deviceIds are populated) and ranks them
 * ascending by tier (0 = best). `excludeIds` removes devices already tried
 * this call (bounds the auto-fallback loop). Sort is stable — ties preserve
 * `devices`' original order, which is `enumerateDevices()`'s (implementation
 * -defined but consistent) ordering.
 */
export function rankMicCandidates(
  devices: readonly MediaDeviceInfo[],
  excludeIds: ReadonlySet<string>
): MediaDeviceInfo[] {
  const outputGroupIds = buildOutputGroupIds(devices);

  return devices
    .filter(
      (d) =>
        d.kind === 'audioinput' && !!d.deviceId && !excludeIds.has(d.deviceId)
    )
    .sort(
      (a, b) =>
        fallbackTier(a, outputGroupIds) - fallbackTier(b, outputGroupIds)
    );
}

// ─── Display classification (device menu, Turn-12 12a) ─────────
//
// The mic switcher's device menu groups and iconifies devices. Derived from
// the SAME regexes/tier function as the fallback ranking above — a single
// source of truth, so the menu can never disagree with the auto-fallback
// about what a device is.

export const MicDeviceGroup = {
  /** Shares a groupId with an audiooutput — a headset (ranking tier 0). */
  HEADSET: 'headset',
  /** Plain mic whose label reads as the machine's own (tier 1 + built-in
   *  label) — gets the menu's "Built-in" sublabel. */
  BUILT_IN: 'builtIn',
  /** Any other plain mic (tier 1). */
  STANDARD: 'standard',
  /** Continuity/iPhone/iPad (tier 2). */
  PHONE: 'phone',
  /** Virtual/loopback (tier 3) — the menu's separated, dimmed
   *  "Virtual & loopback" group. */
  VIRTUAL: 'virtual',
} as const;
export type MicDeviceGroup =
  (typeof MicDeviceGroup)[keyof typeof MicDeviceGroup];

/**
 * Classifies one device given a pre-built output-groupId set — use this
 * (with one buildOutputGroupIds call) when classifying a whole list, so the
 * set isn't rebuilt per device.
 */
export function classifyMicDevice(
  device: MediaDeviceInfo,
  outputGroupIds: ReadonlySet<string>
): MicDeviceGroup {
  switch (fallbackTier(device, outputGroupIds)) {
    case 3:
      return MicDeviceGroup.VIRTUAL;
    case 2:
      return MicDeviceGroup.PHONE;
    case 0:
      return MicDeviceGroup.HEADSET;
    default:
      return BUILTIN_DEVICE_RE.test(device.label)
        ? MicDeviceGroup.BUILT_IN
        : MicDeviceGroup.STANDARD;
  }
}

/**
 * Single-device convenience wrapper. `devices` is the FULL
 * enumerateDevices() list (inputs AND outputs) — the headset check needs the
 * audiooutput groupIds, exactly like rankMicCandidates.
 */
export function micDeviceGroup(
  device: MediaDeviceInfo,
  devices: readonly MediaDeviceInfo[]
): MicDeviceGroup {
  return classifyMicDevice(device, buildOutputGroupIds(devices));
}
